import React, { useCallback, useEffect, useState } from 'react';
import { listRules, createRule, bulkCreateRules, deleteRule, errorMessage } from '../api/securityApi';
import SecurityNav from '../components/Security/SecurityNav';
import SiteSelector, { useActiveSite } from '../components/Security/SiteSelector';
import RequirePermission from '../auth/RequirePermission';

export default function TrafficRulesPage() {
  const { sites, siteId, setSiteId } = useActiveSite();
  const [listType, setListType] = useState('Block');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [res, setRes] = useState({ items: [], totalCount: 0, pageSize: 25 });
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({ ruleType: 'Ip', value: '', reason: '', expiresInMinutes: '', global: false });
  const [bulk, setBulk] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setRes(await listRules({ siteId, listType, search, page, pageSize: 25 })); }
    catch (e) { setError(errorMessage(e)); }
    finally { setLoading(false); }
  }, [siteId, listType, search, page]);
  useEffect(() => { load(); }, [load]);

  const scopeKey = form.global ? null : (siteId || null);
  const expires = form.expiresInMinutes ? Number(form.expiresInMinutes) : null;

  const add = async (e) => {
    e.preventDefault(); setError(''); setInfo('');
    try {
      await createRule({ siteKey: scopeKey, ruleType: form.ruleType, listType, value: form.value.trim(), reason: form.reason || null, expiresInMinutes: expires });
      setForm({ ...form, value: '', reason: '' }); setInfo('Rule added.'); load();
    } catch (err) { setError(errorMessage(err)); }
  };

  const importBulk = async () => {
    setError(''); setInfo('');
    const values = bulk.split(/[\s,;]+/).map((v) => v.trim()).filter(Boolean);
    if (!values.length) return;
    try {
      const r = await bulkCreateRules({ siteKey: scopeKey, ruleType: form.ruleType, listType, values, reason: form.reason || null, expiresInMinutes: expires });
      setInfo(`Created ${r.created}, skipped ${r.skipped}${r.invalid.length ? `, invalid: ${r.invalid.slice(0, 5).join(', ')}` : ''}`);
      setBulk(''); load();
    } catch (err) { setError(errorMessage(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this rule?')) return;
    try { await deleteRule(id); load(); } catch (err) { setError(errorMessage(err)); }
  };

  const pages = Math.max(1, Math.ceil(res.totalCount / (res.pageSize || 25)));
  const placeholder = { Ip: '203.0.113.7', Cidr: '203.0.113.0/24', Fingerprint: 'fp-…', UserAgent: 'text contained in the user agent' }[form.ruleType];

  return (
    <div className="shell">
      <header className="page-header"><div><h1>Block &amp; Allow lists</h1>
        <p className="muted">Blocked traffic is always enforced. Allowlisted traffic is never flagged.</p></div></header>
      <SecurityNav />
      <div className="sec-toolbar">
        <SiteSelector sites={sites} value={siteId} onChange={(v) => { setPage(1); setSiteId(v); }} />
        <select className="sec-select" value={listType} onChange={(e) => { setPage(1); setListType(e.target.value); }}>
          <option value="Block">Blocklist</option><option value="Allow">Allowlist</option></select>
        <input className="sec-input" placeholder="Search value…" value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }} />
      </div>
      {error && <div className="toast error">{error}</div>}
      {info && <div className="toast info">{info}</div>}

      <RequirePermission code="rules.manage">
        <div className="sec-grid">
          <form className="sec-card" onSubmit={add}>
            <h3>Add to {listType === 'Block' ? 'blocklist' : 'allowlist'}</h3>
            <div className="sec-row">
              <select className="sec-select" value={form.ruleType} onChange={(e) => setForm({ ...form, ruleType: e.target.value })}>
                {['Ip', 'Cidr', 'Fingerprint', 'UserAgent'].map((t) => <option key={t}>{t}</option>)}</select>
              <input className="sec-input" style={{ flex: 1 }} required placeholder={placeholder} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
            </div>
            <div className="sec-row" style={{ marginTop: 10 }}>
              <input className="sec-input" placeholder="Reason (optional)" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
              <input className="sec-input" type="number" min="1" placeholder="Expires in (minutes)" value={form.expiresInMinutes} onChange={(e) => setForm({ ...form, expiresInMinutes: e.target.value })} />
            </div>
            <label style={{ display: 'block', margin: '10px 0' }}>
              <input type="checkbox" checked={form.global} onChange={(e) => setForm({ ...form, global: e.target.checked })} /> Apply to all my sites
            </label>
            <p className="muted" style={{ fontSize: 12 }}>Scope: {form.global || !siteId ? 'all sites' : 'selected site only'}</p>
            <button className="primary" type="submit">Add rule</button>
          </form>
          <div className="sec-card">
            <h3>Bulk import</h3>
            <textarea className="sec-input" rows="5" style={{ width: '100%' }} placeholder="Paste values separated by new lines, spaces or commas" value={bulk} onChange={(e) => setBulk(e.target.value)} />
            <p className="muted" style={{ fontSize: 12 }}>Uses the rule type, reason, expiry and scope from the form.</p>
            <button className="ghost" onClick={importBulk}>Import</button>
          </div>
        </div>
      </RequirePermission>

      <div className="sec-card sec-scroll">
        <table className="sec-table">
          <thead><tr><th>Type</th><th>Value</th><th>Scope</th><th>Reason</th><th>Source</th><th>Expires</th><th /></tr></thead>
          <tbody>
            {res.items.map((r) => (
              <tr key={r.id}>
                <td>{r.ruleType}</td><td className="sec-mono">{r.value}</td>
                <td>{r.siteKey ? <span className="sec-mono">{r.siteKey.slice(0, 14)}…</span> : 'All sites'}</td>
                <td className="muted">{r.reason}</td><td>{r.source}</td>
                <td>{r.expiresAt ? new Date(r.expiresAt + (String(r.expiresAt).endsWith('Z') ? '' : 'Z')).toLocaleString() : 'Never'}</td>
                <td><RequirePermission code="rules.manage"><button className="ghost danger" onClick={() => remove(r.id)}>Remove</button></RequirePermission></td>
              </tr>
            ))}
            {!loading && res.items.length === 0 && <tr><td colSpan="7" className="muted">Nothing here yet.</td></tr>}
          </tbody>
        </table>
        <div className="sec-pager">
          <span className="muted">{res.totalCount} total</span>
          <button className="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
          <span>{page} / {pages}</span>
          <button className="ghost" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      </div>
    </div>
  );
}
